/**
 * HopDrive Configuration Types
 *
 * Shared type definitions for the configs package infrastructure.
 * Contains constants and interfaces for the definition system.
 *
 * Note: Config-specific types (HopDriveConfig, Branding, etc.) are defined
 * via Zod schemas in their respective definition and schema files.
 */

import { z } from 'zod';
import type { HopDriveConfig } from './_generated/hopdrive-config-schema.js';
import type { StorageConfig } from './storage-types.js';

// =============================================================================
// Config Level Registry - Single Source of Truth
// =============================================================================

/**
 * Database table information for a config level.
 * Only levels with database tables have this defined.
 */
export interface ConfigLevelTableInfo {
  /** GraphQL/database table name (e.g., 'customers') */
  tableName: string;
  /** Primary key field name (usually 'id') */
  idField: string;
  /** Optional name field for better reporting (e.g., 'name') */
  nameField?: string;
}

/**
 * Complete metadata for a config level.
 * This is the single source of truth for all level information.
 */
export interface ConfigLevelInfo {
  /** Level identifier (e.g., 'hopdrive', 'customer') */
  level: string;
  /** Human-readable label for display */
  label: string;
  /** Merge priority - lower values are overridden by higher values */
  priority: number;
  /** Database table info (undefined for HOPDRIVE which has no table) */
  table?: ConfigLevelTableInfo;
}

/**
 * Config Level Registry - The single source of truth for all config levels.
 *
 * This registry defines:
 * - All available config levels
 * - Their merge priority order (lower = overridden by higher)
 * - Associated database tables (if any)
 *
 * The HOPDRIVE level is special: it has no database table because
 * it represents the hardcoded default configuration.
 */
export const CONFIG_LEVELS_REGISTRY: readonly ConfigLevelInfo[] = [
  {
    level: 'hopdrive',
    label: 'HopDrive Defaults',
    priority: 0,
    // No table - defaults are hardcoded
  },
  {
    level: 'region',
    label: 'Region',
    priority: 1,
    table: { tableName: 'regions', idField: 'id', nameField: 'name' },
  },
  {
    level: 'organization',
    label: 'Organization',
    priority: 2,
    table: { tableName: 'organizations', idField: 'id', nameField: 'name' },
  },
  {
    level: 'customer',
    label: 'Customer',
    priority: 3,
    table: { tableName: 'customers', idField: 'id', nameField: 'name' },
  },
  {
    level: 'workflowset',
    label: 'Workflow Set',
    priority: 4,
    table: { tableName: 'workflowsets', idField: 'id', nameField: 'name' },
  },
  {
    level: 'raterulegroup',
    label: 'Rate Rule Group',
    priority: 5,
    table: { tableName: 'raterulegroups', idField: 'id', nameField: 'name' },
  },
  {
    level: 'move',
    label: 'Move',
    priority: 6,
    table: { tableName: 'moves', idField: 'id' },
  },
] as const;

// =============================================================================
// Derived Helpers
// =============================================================================

/**
 * Config hierarchy level string literals.
 * Provides convenient access to level values.
 *
 * @example
 * import { LEVELS } from '@hopdrive/sdk-configs'
 * const level = LEVELS.CUSTOMER // 'customer'
 */
export const LEVELS = {
  HOPDRIVE: 'hopdrive',
  REGION: 'region',
  ORGANIZATION: 'organization',
  CUSTOMER: 'customer',
  WORKFLOWSET: 'workflowset',
  RATERULEGROUP: 'raterulegroup',
  MOVE: 'move',
} as const;

export type ConfigLevel = (typeof LEVELS)[keyof typeof LEVELS];

/**
 * All config levels in merge priority order (lowest to highest).
 */
export const ALL_CONFIG_LEVELS: ConfigLevel[] = CONFIG_LEVELS_REGISTRY.slice()
  .sort((a, b) => a.priority - b.priority)
  .map(info => info.level as ConfigLevel);

// =============================================================================
// Table Configuration Helpers
// =============================================================================

/**
 * Flattened table info for a config level.
 * Used by database operations like scanning and key rotation.
 */
export interface ConfigTableInfo {
  /** GraphQL/database table name (e.g., 'customers') */
  tableName: string;
  /** Config level this table represents */
  level: ConfigLevel;
  /** Primary key field name (usually 'id') */
  idField: string;
  /** Optional name field for better reporting (e.g., 'name') */
  nameField?: string;
}

/**
 * All database tables that have config jsonb columns.
 * Excludes HOPDRIVE which has no database table.
 */
export const CONFIG_TABLES: ConfigTableInfo[] = CONFIG_LEVELS_REGISTRY.filter(
  (info): info is ConfigLevelInfo & { table: ConfigLevelTableInfo } => info.table !== undefined
).map(info => ({
  tableName: info.table.tableName,
  level: info.level as ConfigLevel,
  idField: info.table.idField,
  nameField: info.table.nameField,
}));

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get full level info from the registry.
 */
export function getConfigLevelInfo(level: ConfigLevel): ConfigLevelInfo | undefined {
  return CONFIG_LEVELS_REGISTRY.find(info => info.level === level);
}

/**
 * Get the table configuration for a specific config level.
 * Returns undefined for HOPDRIVE (which has no database table).
 */
export function getConfigTableForLevel(level: ConfigLevel): ConfigTableInfo | undefined {
  return CONFIG_TABLES.find(t => t.level === level);
}

/**
 * Check if a level has an associated database table.
 */
export function levelHasTable(level: ConfigLevel): boolean {
  const info = getConfigLevelInfo(level);
  return info?.table !== undefined;
}

// =============================================================================
// Definition Categories
// =============================================================================

/**
 * Category information for grouping definitions in UIs.
 * Each category has a key (used in definitions), label (for display), and description.
 */
export const CATEGORIES = {
  METADATA: {
    key: 'metadata',
    label: 'Metadata',
    description: 'Schema versioning and system metadata',
  },
  APPOINTMENTS: {
    key: 'appointments',
    label: 'Appointments',
    description: 'Appointment scheduling and management settings',
  },
  APPOINTMENT_MANAGEMENT: {
    key: 'appointment-management',
    label: 'Appointment Management',
    description: 'Advanced appointment management and workflow configuration',
  },
  APPRAISALS: {
    key: 'appraisals',
    label: 'Appraisals',
    description: 'Vehicle appraisal configuration and settings',
  },
  BILLING: {
    key: 'billing',
    label: 'Billing',
    description: 'Billing, invoicing, and payment configuration',
  },
  BRANDING: {
    key: 'branding',
    label: 'Branding',
    description: 'White-label branding and visual customization settings',
  },
  CUSTOM_SETTINGS: {
    key: 'custom-settings',
    label: 'Custom Settings',
    description: 'Custom configuration values and settings',
  },
  DMS: {
    key: 'dms',
    label: 'DMS',
    description: 'Dealer Management System integration configuration',
  },
  EMAIL_SCRAPING: {
    key: 'email-scraping',
    label: 'Email Scraping',
    description: 'Email scraping and parsing configuration',
  },
  FRAUD_AUDIT: {
    key: 'fraud-audit',
    label: 'Fraud & Audit',
    description: 'Fraud detection and audit trail configuration',
  },
  FUEL: {
    key: 'fuel',
    label: 'Fuel',
    description: 'Fuel pricing and calculation settings',
  },
  HOPDRIVE_CONTACTS: {
    key: 'hopdrive-contacts',
    label: 'HopDrive Contacts',
    description: 'HopDrive contact information and support settings',
  },
  INTEGRATIONS: {
    key: 'integrations',
    label: 'Integrations',
    description: 'External integration configuration and settings',
  },
  MOVE_PLANNING: {
    key: 'move-planning',
    label: 'Move Planning',
    description: 'Move planning, estimation, and move type configuration',
  },
  NOTIFICATIONS: {
    key: 'notifications',
    label: 'Notifications',
    description: 'Email and notification preferences',
  },
  OPERATIONAL_STATE: {
    key: 'operational-state',
    label: 'Operational State',
    description: 'System-managed operational state written by backend processes',
  },
  PRICING: {
    key: 'pricing',
    label: 'Pricing & Fees',
    description: 'Pricing and fee configuration',
  },
  PRIVATE: {
    key: 'private',
    label: 'Private',
    description: 'Private configuration settings',
  },
  REFERENCE_NUMBER: {
    key: 'reference-number',
    label: 'Reference Number',
    description: 'Reference number settings and requirements',
  },
  REGION_PIPELINE: {
    key: 'region-pipeline',
    label: 'Region Pipeline',
    description: 'Region pipeline configuration and settings',
  },
  RIDESHARE: {
    key: 'rideshare',
    label: 'Rideshare',
    description: 'Rideshare integration and calling configuration',
  },
  SERVICE_RADIUS: {
    key: 'service-radius',
    label: 'Service Radius',
    description: 'Service area radius and eligibility configuration',
  },
  THIRD_PARTY_PAYER: {
    key: 'third-party-payer',
    label: 'Third Party Payer',
    description: 'Third-party payer configuration and default settings',
  },
  WORKFLOWS: {
    key: 'workflows',
    label: 'Workflows',
    description: 'Workflow configuration and routing settings',
  },
} as const;

export type DefinitionCategory = (typeof CATEGORIES)[keyof typeof CATEGORIES]['key'];

// =============================================================================
// Definition Kinds
// =============================================================================

/**
 * Kind constants for distinguishing between user-configurable settings
 * and system-managed operational state.
 */
export const DEFINITION_KINDS = {
  /** User-configurable setting that can be set via UI/API (default) */
  SETTING: 'setting',
  /** System-managed operational state written by backend processes */
  OUTPUT: 'output',
} as const;

export type DefinitionKind = (typeof DEFINITION_KINDS)[keyof typeof DEFINITION_KINDS];

// =============================================================================
// Roles
// =============================================================================

/**
 * Role constants based on roletypes table in the database.
 * These roles determine which users can read/view specific config definitions.
 */
export const ROLES = {
  ADMIN: 'admin',
  DISPATCHER: 'dispatcher',
  DRIVER: 'driver',
  CONSUMER: 'consumer',
  DEALER_ADMIN: 'dealer-admin',
  DEALER: 'dealer',
  DEALER_USER: 'dealer_user',
  DEALER_MANAGER: 'dealer_manager',
  DEALER_ADMIN_ORG: 'dealer_admin',
  DEALER_SUPER_ADMIN: 'dealer-super-admin',
  PENDING_DRIVER: 'pending-driver',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

// =============================================================================
// Definition Interface & Helper
// =============================================================================

/**
 * A schema definition for a root-level config property.
 * Each definition has a Zod schema, allowed levels, and metadata.
 */
export interface Definition<T extends z.ZodTypeAny = z.ZodTypeAny> {
  /** The config property key (e.g., 'api_enabled', 'branding') */
  key: string;
  /** Zod schema for validation */
  schema: T;
  /** Human-readable label for on-screen presentation */
  label: string;
  /** Human-readable description for documentation and UIs */
  description: string;
  /** Warning message for sensitive or complex definitions */
  warning?: string;
  /** Whether this is a user setting or system output. */
  kind: DefinitionKind;
  /** Category for grouping in UIs */
  category?: DefinitionCategory;
  /** Which config levels can have this definition written at */
  writableAtLevels: readonly ConfigLevel[];
  /** Which roles are allowed to read/view this definition in composite configs. Must include ADMIN. */
  readableByRoles: readonly Role[];
  /** Default value (used in HOPDRIVE_DEFAULT_CONFIG) */
  defaultValue?: z.infer<T>;
  /** Whether this definition is deprecated and should not be used for new configurations */
  deprecated?: boolean;
  /** Storage configuration for non-JSONB storage and enum sources */
  storage?: StorageConfig;
}

/**
 * Helper to define a config schema definition with full type inference.
 * Ensures consistent structure and provides autocomplete.
 * Validates that readableByRoles includes ADMIN at runtime.
 *
 * @example
 * export const definition = define({
 *   key: 'api_enabled',
 *   schema,
 *   writableAtLevels: [LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
 *   readableByRoles: [ROLES.ADMIN, ROLES.DEALER_ADMIN],
 *   label: 'API Enabled',
 *   description: 'Whether API access is enabled.',
 *   category: CATEGORIES.INTEGRATIONS.key,
 *   defaultValue: false,
 * })
 */
export function define<T extends z.ZodTypeAny>(definition: Definition<T>): Definition<T> {
  // Runtime validation: ensure ADMIN is in the array
  // This will throw an error during development/testing if ADMIN is missing
  if (!definition.readableByRoles.includes(ROLES.ADMIN)) {
    throw new Error(
      `Definition "${definition.key}" must include ROLES.ADMIN in readableByRoles. ` +
        `Received: [${definition.readableByRoles.join(', ')}]`
    );
  }
  return definition;
}

// =============================================================================
// Fetcher Types - Used by config fetchers and utilities
// =============================================================================

/**
 * Individual configs from each level (all optional since any level may not exist)
 */
export interface ConfigLevels {
  [LEVELS.HOPDRIVE]: HopDriveConfig | null;
  [LEVELS.REGION]: HopDriveConfig | null;
  [LEVELS.ORGANIZATION]: HopDriveConfig | null;
  [LEVELS.CUSTOMER]: HopDriveConfig | null;
  [LEVELS.WORKFLOWSET]: HopDriveConfig | null;
  [LEVELS.RATERULEGROUP]: HopDriveConfig | null;
  [LEVELS.MOVE]: HopDriveConfig | null;
}

/**
 * Per-setting metadata from TypeScript setting definitions.
 * Includes schema, permissions, and the source level from SQL.
 */
export interface SettingMetadata {
  /** Simplified JSON schema for the setting */
  schema: Record<string, unknown> | null;
  /** Human-readable label for on-screen presentation */
  label: string;
  /** Human-readable description */
  description: string;
  /** Warning message for sensitive settings */
  warning?: string;
  /** Whether this is a user setting or system output */
  kind: DefinitionKind;
  /** Category for grouping in UIs */
  category?: string;
  /** Which config levels can have this setting written at */
  writableAtLevels: readonly ConfigLevel[];
  /** Which roles are allowed to read this setting */
  readableByRoles: readonly Role[];
  /** Default value if applicable */
  defaultValue?: unknown;
  /** Whether this setting is deprecated and should not be used for new configurations */
  deprecated?: boolean;
  /** Which level this setting's value came from (from SQL sourceLevels) */
  sourceLevel: ConfigLevel;
}

/**
 * SQL metadata from effective_config when include_metadata=true.
 * This is the raw format returned by the database.
 */
export interface SqlMetadata {
  /** Which level this config was fetched for */
  level: ConfigLevel;
  /** The entity ID that was queried */
  entityId: number | bigint;
  /** Individual config from each level */
  levels: Partial<ConfigLevels>;
  /** Maps each setting key to the level it came from */
  sourceLevels: Record<string, ConfigLevel>;
}

/**
 * Full metadata object combining SQL data with TypeScript enrichment.
 * Extends SQL metadata with per-setting TypeScript definitions.
 */
export interface ConfigMetadata extends SqlMetadata {
  /** Per-setting metadata keyed by config property id */
  settings: Record<string, SettingMetadata>;
}

/**
 * Effective config with optional metadata.
 * This is the return type for all fetcher functions.
 *
 * - Without metadata: Just the merged HopDriveConfig
 * - With metadata: HopDriveConfig with __metadata property containing enriched metadata
 */
export interface EffectiveConfig extends HopDriveConfig {
  /** Metadata about the config (only present when includeMetadata=true) */
  __metadata?: ConfigMetadata;
}
